<template>
  <div>
    <div class="air__utils__heading">
      <h5>Teacher Progress</h5>
    </div>

    <a-card :bordered="false" class="mb-4">
      <a-spin :spinning="loadingAssignments">
        <a-form layout="vertical">
          <div class="row">
            <!-- Teacher Selection -->
            <div class="col-md-4">
              <a-form-item label="Teacher">
                <a-select
                  v-model="wizard.teacher_id"
                  placeholder="Select Teacher"
                  show-search
                  :filter-option="filterOption"
                  @change="onTeacherChange"
                  :disabled="!isAdmin && trackingMode !== 'open'"
                >
                  <a-select-option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                    {{ teacher.first_name }} {{ teacher.last_name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </div>

            <!-- Subject Selection (auto-selected if only 1) -->
            <div class="col-md-4">
              <a-form-item label="Subject">
                <a-select
                  v-model="wizard.subject_id"
                  placeholder="Select Subject"
                  @change="onSubjectChange"
                  :disabled="subjectDisabled"
                >
                  <a-select-option v-for="sub in subjects" :key="sub.id" :value="sub.id">
                    {{ sub.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </div>

            <!-- Lesson Plan Selection -->
            <div class="col-md-4">
              <a-form-item label="Lesson Plan">
                <a-select
                  v-model="wizard.plan_id"
                  placeholder="Select Lesson Plan"
                  @change="onPlanChange"
                  :disabled="!wizard.subject_id || filteredPlans.length === 0"
                >
                  <a-select-option v-for="plan in filteredPlans" :key="plan.id" :value="plan.id">
                    {{ plan.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </div>

            <!-- Section Selection -->
            <div class="col-md-4">
              <a-form-item label="Section">
                <a-select
                  v-model="wizard.section_id"
                  placeholder="Select Section"
                  @change="onSectionChange"
                  :disabled="!wizard.plan_id || filteredSections.length === 0"
                >
                  <a-select-option v-for="sec in filteredSections" :key="sec.id" :value="sec.id">
                    {{ sec.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </div>

            <!-- Period Selection -->
            <div class="col-md-4">
              <a-form-item label="Period">
                <a-select
                  v-model="wizard.period"
                  placeholder="Select Period"
                  @change="onPeriodChange"
                  :disabled="!wizard.section_id"
                >
                  <a-select-option :value="1">1st Period</a-select-option>
                  <a-select-option :value="2">2nd Period</a-select-option>
                  <a-select-option :value="3">3rd Period</a-select-option>
                  <a-select-option :value="4">4th Period</a-select-option>
                  <a-select-option :value="5">5th Period</a-select-option>
                  <a-select-option :value="6">6th Period</a-select-option>
                </a-select>
              </a-form-item>
            </div>
          </div>
        </a-form>
      </a-spin>
    </a-card>

    <!-- Lesson Plan Display -->
    <a-card :bordered="false" v-if="wizard.period && wizard.plan_id">
      <a-spin :spinning="loadingPlan">
        <div v-if="!lessonPlan" class="text-center p-4">
          <a-empty description="No lesson plan data found" />
        </div>
        <div v-else>
          <div class="mb-4">
            <h5 class="mb-1">{{ lessonPlan.name }}</h5>
            <div class="text-muted" v-html="lessonPlan.description"></div>
          </div>

          <a-table
            :dataSource="lessonPlan.topics"
            rowKey="id"
            :pagination="false"
            :columns="topicColumns"
            :expandedRowRender="renderSubtopics"
          >
            <template slot="status" slot-scope="status">
              <a-tag :color="getStatusColor(status)">{{ status || 'Not Started' }}</a-tag>
            </template>
            <template slot="actions" slot-scope="text, record">
              <a-button type="info" size="small" class="mr-2" @click="openViewLogsModal(record, 'topic')">
                View Details
              </a-button>
              <a-button type="primary" size="small" @click="openUpdateModal(record, 'topic')">
                Update Progress
              </a-button>
            </template>
          </a-table>
        </div>
      </a-spin>
    </a-card>

    <update-modal
      :visible="modalVisible"
      :item="selectedItem"
      :wizardData="wizardDataForModal"
      @close="modalVisible = false"
      @updated="onProgressUpdated"
    />

    <view-logs-modal
      :visible="viewLogsVisible"
      :item="selectedItem"
      @close="viewLogsVisible = false"
      @edit-log="openEditModal"
    />

    <edit-progress-modal
      :visible="editModalVisible"
      :progressLog="selectedLogForEdit"
      :lessonPlan="lessonPlan"
      @close="editModalVisible = false"
      @updated="onProgressUpdated"
    />
  </div>
</template>

<script>
import UpdateModal from '@/components/teacherprogresswizard/UpdateModal'
import ViewLogsModal from '@/components/teacherprogress/Form/viewTeacherLog'
import EditProgressModal from '@/components/teacherprogress/Form/editUpdateProgress'

export default {
  components: { UpdateModal, ViewLogsModal, EditProgressModal },
  data() {
    return {
      isAdmin: false,
      teachers: [],

      // Raw data from backend (new subject-first structure)
      subjects: [],
      plans: [],
      sectionsByClass: {},
      trackingMode: 'free',

      wizard: {
        teacher_id: undefined,
        subject_id: undefined,
        plan_id: undefined,
        section_id: undefined,
        period: undefined,
      },

      loadingAssignments: false,
      loadingPlan: false,
      lessonPlan: null,

      modalVisible: false,
      viewLogsVisible: false,
      editModalVisible: false,
      selectedItem: null,
      selectedLogForEdit: null,

      topicColumns: [
        { title: 'Topic Name', dataIndex: 'topic_name', key: 'topic_name' },
        { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
        { title: 'Status', dataIndex: 'status', key: 'status', scopedSlots: { customRender: 'status' }, align: 'center', width: 120 },
        { title: 'Action', key: 'actions', scopedSlots: { customRender: 'actions' }, align: 'right', width: 300 },
      ],
    }
  },
  computed: {
    // Subject dropdown is disabled if admin hasn't selected a teacher,
    // or if not admin and no subjects are loaded yet
    subjectDisabled() {
      if (this.isAdmin && !this.wizard.teacher_id) return true
      return this.subjects.length === 0
    },
    // Lesson plans filtered by selected subject
    filteredPlans() {
      if (!this.wizard.subject_id) return []
      return this.plans.filter(p => p.subject_id === this.wizard.subject_id)
    },
    // Sections filtered by the class of the selected lesson plan
    filteredSections() {
      if (!this.wizard.plan_id) return []
      const plan = this.plans.find(p => p.id === this.wizard.plan_id)
      if (!plan) return []
      return this.sectionsByClass[plan.class_id] || []
    },
    // Build wizardData for the UpdateModal (pass class_id from selected plan)
    wizardDataForModal() {
      const plan = this.plans.find(p => p.id === this.wizard.plan_id)
      return {
        teacher_id: this.wizard.teacher_id,
        class_id: plan ? plan.class_id : undefined,
        subject_id: this.wizard.subject_id,
        section_id: this.wizard.section_id,
        period: this.wizard.period,
      }
    },
  },
  mounted() {
    const user = this.$store.getters.user
    this.isAdmin = ['superadmin', 'admin'].includes(user.role)

    this.fetchTeachers().then(() => {
      if (!this.isAdmin && user && user.id) {
        const currentStaff = this.teachers.find(s => Number(s.user_id) === Number(user.id))
        if (currentStaff) {
          this.wizard.teacher_id = currentStaff.id
        }
      }
      this.fetchAssignments()
    })
  },
  methods: {
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    fetchTeachers() {
      return this.$http.get('/api/staff', { params: { dropdown: 1 } }).then(({ data }) => {
        this.teachers = data.data
      })
    },
    fetchAssignments() {
      if (!this.wizard.teacher_id) return

      this.loadingAssignments = true
      this.$http.get('/api/lesson-plan/teacher/assignments', {
        params: { teacher_id: this.wizard.teacher_id },
      }).then(({ data }) => {
        const d = data.data
        this.subjects = d.subjects || []
        this.plans = d.plans || []
        this.sectionsByClass = d.sections_by_class || {}
        this.trackingMode = d.mode || 'free'

        // Auto-select subject if teacher has only 1 assigned subject
        if (this.subjects.length === 1) {
          this.wizard.subject_id = this.subjects[0].id
          this.autoSelectPlanIfStrict()
        }
      }).catch(() => {
        this.$notification.error({ message: 'Failed to fetch assignments' })
      }).finally(() => {
        this.loadingAssignments = false
      })
    },
    // In strict mode, auto-select lesson plan if only 1 valid plan for the selected subject
    autoSelectPlanIfStrict() {
      if (this.trackingMode === 'strict') {
        const plansForSubject = this.plans.filter(p => p.subject_id === this.wizard.subject_id)
        if (plansForSubject.length === 1) {
          this.wizard.plan_id = plansForSubject[0].id
        }
      }
    },
    onTeacherChange() {
      this.resetWizard()
      this.fetchAssignments()
    },
    onSubjectChange() {
      this.wizard.plan_id = undefined
      this.wizard.section_id = undefined
      this.wizard.period = undefined
      this.lessonPlan = null
      this.autoSelectPlanIfStrict()
    },
    onPlanChange() {
      this.wizard.section_id = undefined
      this.wizard.period = undefined
      this.lessonPlan = null
    },
    onSectionChange() {
      this.wizard.period = undefined
      this.lessonPlan = null
    },
    onPeriodChange() {
      // All selections are now complete — load the lesson plan details
      this.loadLessonPlan()
    },
    loadLessonPlan() {
      if (!this.wizard.plan_id || !this.wizard.section_id || !this.wizard.period) return

      const plan = this.plans.find(p => p.id === this.wizard.plan_id)
      if (!plan) return

      this.loadingPlan = true
      this.$http.get(`/api/lesson-plan/teacher/lesson-plans/${plan.id}`).then(({ data }) => {
        const lp = data.data
        if (lp.topics) {
          lp.topics.forEach(topic => {
            const topicLogs = (topic.progress_logs || []).filter(l => Number(l.section_id) === Number(this.wizard.section_id))
            topicLogs.sort((a, b) => b.id - a.id)
            const log = topicLogs.length > 0 ? topicLogs[0] : null

            topic.status = log ? log.status : 'Not Started'
            topic.remarks = log ? log.remarks : ''
            topic.progress_log = log

            if (topic.sub_topics) {
              topic.sub_topics.forEach(sub => {
                const subLogs = (sub.progress_logs || []).filter(l => Number(l.section_id) === Number(this.wizard.section_id))
                subLogs.sort((a, b) => b.id - a.id)
                const subLog = subLogs.length > 0 ? subLogs[0] : null

                sub.status = subLog ? subLog.status : 'Not Started'
                sub.remarks = subLog ? subLog.remarks : ''
                sub.progress_log = subLog
              })
            }
          })
        }
        this.lessonPlan = lp
      }).catch(() => {
        this.$notification.error({ message: 'Failed to load lesson plan' })
      }).finally(() => {
        this.loadingPlan = false
      })
    },
    onProgressUpdated() {
      // Reload lesson plan after a progress update
      this.loadLessonPlan()
    },
    resetWizard() {
      this.wizard.subject_id = undefined
      this.wizard.plan_id = undefined
      this.wizard.section_id = undefined
      this.wizard.period = undefined
      this.lessonPlan = null
      this.subjects = []
      this.plans = []
      this.sectionsByClass = {}
    },
    getStatusColor(status) {
      if (status === 'Completed') return 'green'
      if (status === 'Started') return 'cyan'
      if (status === 'In Progress') return 'orange'
      return 'red'
    },
    openUpdateModal(record, type) {
      this.selectedItem = { ...record, type }
      this.modalVisible = true
    },
    openViewLogsModal(record, type) {
      this.selectedItem = { ...record, type }
      this.viewLogsVisible = true
    },
    openEditModal(log) {
      this.selectedLogForEdit = log
      this.viewLogsVisible = false
      this.editModalVisible = true
    },
    renderSubtopics(record) {
      const subtopics = record.sub_topics || []
      if (!subtopics.length) return null

      return (
        <a-table
          dataSource={subtopics}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            { title: 'Sub-topic Name', dataIndex: 'sub_topic_name', key: 'sub_topic_name' },
            { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              align: 'center',
              width: 120,
              customRender: (text) => (
                <a-tag color={this.getStatusColor(text)}>{text || 'Not Started'}</a-tag>
              ),
            },
            {
              title: 'Action',
              key: 'actions',
              align: 'right',
              width: 300,
              customRender: (text, row) => (
                <div>
                  <a-button type="info" size="small" class="mr-2" onClick={() => this.openViewLogsModal(row, 'sub-topic')}>
                    View Details
                  </a-button>
                  <a-button type="primary" size="small" onClick={() => this.openUpdateModal(row, 'sub-topic')}>
                    Update Progress
                  </a-button>
                </div>
              ),
            },
          ]}
        />
      )
    },
  },
}
</script>

<style scoped>
.text-muted {
  font-size: 0.9em;
  color: #8c8c8c;
}
</style>
